import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import {
  COMMON_TIMEZONES,
  DEFAULT_COLUMNS_PRESET,
  DEFAULT_STATUS_LABELS_PRESET,
  createEmptyColumn,
  createEmptyStatusLabel,
} from '../../utils/reportConfig';
import { reportConfigApi } from '../../api/reportConfig';
const buildDefaultStatusMeta = () =>
  DEFAULT_STATUS_LABELS_PRESET.map(([key, title]) => ({ key, title }));

const DEVICE_TYPE_LABELS = {
  electricity: 'Электросчётчик',
  water: 'Вода (ХВС/ГВС)',
  heating: 'Отопление',
  urt: 'УРТ',
};

const buildDefaultDeviceTypeMeta = () => ({
  electricity: [...DEFAULT_COLUMNS_PRESET],
  water: [...DEFAULT_COLUMNS_PRESET],
  heating: [...DEFAULT_COLUMNS_PRESET],
  urt: [...DEFAULT_COLUMNS_PRESET],
});

const ReportConfigFormSection = ({
  value,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const [timezoneInput, setTimezoneInput] = useState(value?.report_timezone || '');
  const [activeDeviceType, setActiveDeviceType] = useState('electricity');
  const [meta, setMeta] = useState({
    columns: [...DEFAULT_COLUMNS_PRESET],
    status_labels: buildDefaultStatusMeta(),
    device_type_columns: buildDefaultDeviceTypeMeta(),
  });

  const normalizeDeviceTypeMeta = (raw) => {
    const fallback = buildDefaultDeviceTypeMeta();
    if (!raw || typeof raw !== 'object') return fallback;
    return Object.keys(fallback).reduce((acc, key) => {
      const items =
        Array.isArray(raw?.[key]) && raw[key].length ? raw[key] : fallback[key];
      acc[key] = items.map((item) => ({
        key: item?.key || '',
        title: item?.title || item?.key || '',
      }));
      return acc;
    }, {});
  };

  useEffect(() => {
    let mounted = true;
    const loadMeta = async () => {
      try {
        const data = await reportConfigApi.getMeta();
        if (!mounted) return;
        setMeta({
          columns:
            (Array.isArray(data?.columns) && data?.columns.length
              ? data.columns
              : DEFAULT_COLUMNS_PRESET
            ).map((item) => ({ key: item.key, title: item.title })),
          status_labels:
            (Array.isArray(data?.status_labels) && data?.status_labels.length
              ? data.status_labels
              : buildDefaultStatusMeta()
            ).map((item) => ({ key: item.key, title: item.title })),
          device_type_columns: normalizeDeviceTypeMeta(data?.device_type_columns),
        });
      } catch {
        if (!mounted) return;
        setMeta({
          columns: [...DEFAULT_COLUMNS_PRESET],
          status_labels: buildDefaultStatusMeta(),
          device_type_columns: buildDefaultDeviceTypeMeta(),
        });
      }
    };
    loadMeta();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setTimezoneInput(value?.report_timezone || '');
  }, [value?.report_timezone]);

  const safeOnChange = (next) => {
    onChange?.(next);
  };

  const handleRootField = (field) => (event) => {
    const fieldValue = event?.target?.value ?? '';
    safeOnChange({ ...value, [field]: fieldValue });
  };

  const updateColumns = (columns) => {
    safeOnChange({ ...value, columns });
  };

  const moveItem = (list, fromIndex, toIndex) => {
    if (!Array.isArray(list)) return list;
    if (toIndex < 0 || toIndex >= list.length) return list;
    const next = [...list];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    return next;
  };

  const updateStatuses = (statusLabels) => {
    safeOnChange({ ...value, statusLabels });
  };

  const updateDeviceTypeColumns = (typeKey, columns) => {
    safeOnChange({
      ...value,
      deviceTypeColumns: {
        ...(value?.deviceTypeColumns || {}),
        [typeKey]: columns,
      },
    });
  };

  const editColumn = (id, field, fieldValue) => {
    const columns = (value?.columns || []).map((column) =>
      column.id === id ? { ...column, [field]: fieldValue } : column,
    );
    updateColumns(columns);
  };

  const removeColumn = (id) => {
    const next = (value?.columns || []).filter((column) => column.id !== id);
    updateColumns(next);
  };

  const addColumn = () => {
    updateColumns([...(value?.columns || []), createEmptyColumn()]);
  };

  const moveColumn = (id, delta) => {
    const current = value?.columns || [];
    const index = current.findIndex((column) => column.id === id);
    if (index === -1) return;
    const next = moveItem(current, index, index + delta);
    if (next !== current) updateColumns(next);
  };

  const editStatus = (id, field, fieldValue) => {
    const next = (value?.statusLabels || []).map((status) =>
      status.id === id ? { ...status, [field]: fieldValue } : status,
    );
    updateStatuses(next);
  };

  const removeStatus = (id) => {
    const next = (value?.statusLabels || []).filter((status) => status.id !== id);
    updateStatuses(next);
  };

  const addStatus = () => {
    updateStatuses([...(value?.statusLabels || []), createEmptyStatusLabel()]);
  };

  const editDeviceTypeColumn = (typeKey, id, field, fieldValue) => {
    const columns = (value?.deviceTypeColumns?.[typeKey] || []).map((column) =>
      column.id === id ? { ...column, [field]: fieldValue } : column,
    );
    updateDeviceTypeColumns(typeKey, columns);
  };

  const removeDeviceTypeColumn = (typeKey, id) => {
    const next = (value?.deviceTypeColumns?.[typeKey] || []).filter(
      (column) => column.id !== id,
    );
    updateDeviceTypeColumns(typeKey, next);
  };

  const addDeviceTypeColumn = (typeKey) => {
    updateDeviceTypeColumns(typeKey, [
      ...(value?.deviceTypeColumns?.[typeKey] || []),
      createEmptyColumn(),
    ]);
  };

  const moveDeviceTypeColumn = (typeKey, id, delta) => {
    const current = value?.deviceTypeColumns?.[typeKey] || [];
    const index = current.findIndex((column) => column.id === id);
    if (index === -1) return;
    const next = moveItem(current, index, index + delta);
    if (next !== current) updateDeviceTypeColumns(typeKey, next);
  };

  const columns = value?.columns || [];
  const statuses = value?.statusLabels || [];
  const deviceTypeColumns = value?.deviceTypeColumns || {};

  const columnHints = useMemo(() => {
    const hints: Record<string, string> = {};
    meta.columns.forEach((item) => {
      if (item.key) hints[item.key] = item.title || item.key;
    });
    return hints;
  }, [meta.columns]);

  const statusHints = useMemo(() => {
    const hints: Record<string, string> = {};
    meta.status_labels.forEach((item) => {
      if (item.key) hints[item.key] = item.title || item.key;
    });
    return hints;
  }, [meta.status_labels]);

  const deviceTypeHints = useMemo(() => {
    const hints: Record<string, Record<string, string>> = {};
    Object.entries(meta.device_type_columns || {}).forEach(([typeKey, items]) => {
      hints[typeKey] = {};
      (items || []).forEach((item) => {
        if (item?.key) hints[typeKey][item.key] = item.title || item.key;
      });
    });
    return hints;
  }, [meta.device_type_columns]);

  const columnKeyOptions = useMemo(() => {
    const base = meta.columns.map((item) => item.key).filter(Boolean);
    const extra = columns.map((column) => column.key).filter(Boolean);
    return Array.from(new Set([...base, ...extra]));
  }, [meta.columns, columns]);

  const statusKeyOptions = useMemo(() => {
    const base = meta.status_labels.map((item) => item.key).filter(Boolean);
    const extra = statuses.map((status) => status.key).filter(Boolean);
    return Array.from(new Set([...base, ...extra]));
  }, [meta.status_labels, statuses]);

  const deviceTypeKeyOptions = (typeKey) => {
    const base = (meta.device_type_columns?.[typeKey] || [])
      .map((item) => item.key)
      .filter(Boolean);
    const extra = (deviceTypeColumns[typeKey] || [])
      .map((column) => column.key)
      .filter(Boolean);
    return Array.from(new Set([...base, ...extra]));
  };

  const setColumnKey = (id, newKey, { autoTitle = false } = {}) => {
    const keyValue = (newKey ?? '').trim();
    const nextColumns = (value?.columns || []).map((column) => {
      if (column.id !== id) return column;
      const next = { ...column, key: keyValue };
      if (autoTitle) {
        const suggestedTitle = columnHints[keyValue];
        if (suggestedTitle && !column.title?.trim()) {
          next.title = suggestedTitle;
        }
      }
      return next;
    });
    updateColumns(nextColumns);
  };

  const setStatusKey = (id, newKey, { autoLabel = false } = {}) => {
    const keyValue = (newKey ?? '').trim();
    const nextStatuses = (value?.statusLabels || []).map((status) => {
      if (status.id !== id) return status;
      const next = { ...status, key: keyValue };
      if (autoLabel) {
        const suggested = statusHints[keyValue];
        if (suggested && !status.label?.trim()) {
          next.label = suggested;
        }
      }
      return next;
    });
    updateStatuses(nextStatuses);
  };

  const setDeviceTypeColumnKey = (typeKey, id, newKey, { autoTitle = false } = {}) => {
    const keyValue = (newKey ?? '').trim();
    const hints = deviceTypeHints[typeKey] || {};
    const nextColumns = (value?.deviceTypeColumns?.[typeKey] || []).map((column) => {
      if (column.id !== id) return column;
      const next = { ...column, key: keyValue };
      if (autoTitle) {
        const suggestedTitle = hints[keyValue];
        if (suggestedTitle && !column.title?.trim()) {
          next.title = suggestedTitle;
        }
      }
      return next;
    });
    updateDeviceTypeColumns(typeKey, nextColumns);
  };

  const deviceTypeKeys = Object.keys(DEVICE_TYPE_LABELS);
  const activeDeviceColumns = deviceTypeColumns[activeDeviceType] || [];
  const activeDeviceHints = deviceTypeHints[activeDeviceType] || {};
  const activeDeviceOptions = deviceTypeKeyOptions(activeDeviceType);
  const activeDeviceError = errors?.deviceTypeColumns?.[activeDeviceType] || '';

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Настройки отчёта
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Опорный день"
            type="number"
            value={value?.report_anchor_day ?? ''}
            onChange={handleRootField('report_anchor_day')}
            fullWidth
            inputProps={{ min: 1, max: 31 }}
            disabled={disabled}
            error={Boolean(errors.report_anchor_day)}
            helperText={
              errors.report_anchor_day ||
              'День месяца, на который выбирается последнее показание.'
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Добор, дней"
            type="number"
            value={value?.report_fallback_days ?? ''}
            onChange={handleRootField('report_fallback_days')}
            fullWidth
            inputProps={{ min: 0, max: 31 }}
            disabled={disabled}
            error={Boolean(errors.report_fallback_days)}
            helperText={
              errors.report_fallback_days ||
              'На сколько дней назад от опорной даты искать последнее показание.'
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo
            options={COMMON_TIMEZONES}
            disabled={disabled}
            value={value?.report_timezone || ''}
            inputValue={timezoneInput}
            onInputChange={(_, newValue) => {
              setTimezoneInput(newValue || '');
              safeOnChange({ ...value, report_timezone: newValue || '' });
            }}
            onChange={(_, newValue) => {
              setTimezoneInput(newValue || '');
              safeOnChange({ ...value, report_timezone: newValue || '' });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Часовой пояс"
                fullWidth
                error={Boolean(errors.report_timezone)}
                helperText={
                  errors.report_timezone ||
                  'Например, Europe/Moscow. Можно ввести вручную.'
                }
              />
            )}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Формат отчёта
        </Typography>
        <RadioGroup
          row
          value={value?.export_format || 'xlsx'}
          onChange={handleRootField('export_format')}
        >
          <FormControlLabel
            value="xlsx"
            control={<Radio size="small" />}
            label="Excel"
            disabled={disabled}
          />
          <FormControlLabel
            value="c300_csv"
            control={<Radio size="small" />}
            label="CSV для С-300"
            disabled={disabled}
          />
        </RadioGroup>
        <Typography variant="body2" color="text.secondary">
          {value?.export_format === 'c300_csv'
            ? 'Файл для загрузки в С-300. Этот формат используется и в письмах. Для УРТ файл также доступен в расчёте отопления.'
            : 'Отчёт BillingService с выбранными колонками. Этот формат используется и в письмах.'}
        </Typography>

        <Divider sx={{ my: 2 }} />
        {value?.export_format === 'c300_csv' ? (
          <Typography variant="body2" color="text.secondary">
            Для каждого ресурса создаётся отдельный CSV. Если файлов несколько,
            они скачиваются и отправляются на почту в ZIP-архиве.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Файлы
            </Typography>
            <RadioGroup
              row
              value={value?.export_mode || 'combined'}
              onChange={handleRootField('export_mode')}
            >
              <FormControlLabel
                value="combined"
                control={<Radio size="small" />}
                label="Один файл"
                disabled={disabled}
              />
              <FormControlLabel
                value="separate_by_resource"
                control={<Radio size="small" />}
                label="Отдельно по ресурсам"
                disabled={disabled}
              />
            </RadioGroup>
            <Typography variant="body2" color="text.secondary">
              Вода, тепло и электричество будут сохранены в отдельных файлах.
            </Typography>
          </>
        )}
      </Paper>

      {value?.export_format !== 'c300_csv' && (
        <>
      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Колонки отчёта
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '35%' }}>Ключ поля</TableCell>
              <TableCell>Заголовок в отчёте</TableCell>
              <TableCell align="right" sx={{ width: 72 }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {columns.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  Колонки не выбраны.
                </TableCell>
              </TableRow>
            )}
            {columns.map((column, columnIndex) => (
              <TableRow key={column.id}>
                <TableCell>
                  <Autocomplete
                    freeSolo
                    options={columnKeyOptions}
                    value={column.key || ''}
                    onInputChange={(_, newValue) => setColumnKey(column.id, newValue || '')}
                    onChange={(_, newValue) =>
                      setColumnKey(
                        column.id,
                        typeof newValue === 'string' ? newValue : newValue || '',
                        { autoTitle: true }
                      )
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div>
                          <div>{option}</div>
                          {columnHints[option] && (
                            <Typography variant="caption" color="text.secondary">
                              {columnHints[option]}
                            </Typography>
                          )}
                        </div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ключ поля"
                        size="small"
                        fullWidth
                        disabled={disabled}
                        error={Boolean(errors.columns)}
                        placeholder="napr., apartment_number"
                      />
                    )}
                    disabled={disabled}
                    clearOnBlur={false}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={column.title}
                    onChange={(event) => editColumn(column.id, 'title', event.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    error={Boolean(errors.columns) && !column.title?.trim()}
                    placeholder="Например, № квартиры"
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                    <IconButton
                      aria-label="Переместить вверх"
                      onClick={() => moveColumn(column.id, -1)}
                      disabled={disabled || columnIndex === 0}
                      size="small"
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="Переместить вниз"
                      onClick={() => moveColumn(column.id, 1)}
                      disabled={disabled || columnIndex === columns.length - 1}
                      size="small"
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="Удалить колонку"
                      onClick={() => removeColumn(column.id)}
                      disabled={disabled}
                      size="small"
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {errors.columns && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {errors.columns}
        </Typography>
      )}
      <Button
        startIcon={<AddCircleOutlineRoundedIcon />}
        variant="outlined"
        size="small"
        onClick={addColumn}
        disabled={disabled}
        sx={{ mb: 3 }}
      >
        Добавить колонку
      </Button>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Подписи статусов
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '35%' }}>Ключ статуса</TableCell>
              <TableCell>Подпись</TableCell>
              <TableCell align="right" sx={{ width: 72 }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statuses.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  Нет настроенных статусов. Добавьте хотя бы один ключ и подпись.
                </TableCell>
              </TableRow>
            )}
            {statuses.map((status) => (
              <TableRow key={status.id}>
                <TableCell>
                  <Autocomplete
                    freeSolo
                    options={statusKeyOptions}
                    value={status.key || ''}
                    onInputChange={(_, newValue) => setStatusKey(status.id, newValue || '')}
                    onChange={(_, newValue) =>
                      setStatusKey(
                        status.id,
                        typeof newValue === 'string' ? newValue : newValue || '',
                        { autoLabel: true }
                      )
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div>
                          <div>{option}</div>
                          {statusHints[option] && (
                            <Typography variant="caption" color="text.secondary">
                              {statusHints[option]}
                            </Typography>
                          )}
                        </div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ключ статуса"
                        size="small"
                        fullWidth
                        disabled={disabled}
                        error={Boolean(errors.statusLabels)}
                        placeholder="napr., ok"
                      />
                    )}
                    disabled={disabled}
                    clearOnBlur={false}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={status.label}
                    onChange={(event) => editStatus(status.id, 'label', event.target.value)}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    error={Boolean(errors.statusLabels) && !status.label?.trim()}
                    placeholder="Например, Принято"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="Удалить статус"
                    onClick={() => removeStatus(status.id)}
                    disabled={disabled}
                    size="small"
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {errors.statusLabels && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {errors.statusLabels}
        </Typography>
      )}
      <Button
        startIcon={<AddCircleOutlineRoundedIcon />}
        variant="outlined"
        size="small"
        onClick={addStatus}
        disabled={disabled}
      >
        Добавить статус
      </Button>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Колонки по типам устройств
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Если ничего не выбрано, используются общие колонки.
      </Typography>
      <Tabs
        value={activeDeviceType}
        onChange={(_, next) => setActiveDeviceType(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        {deviceTypeKeys.map((typeKey) => (
          <Tab key={typeKey} value={typeKey} label={DEVICE_TYPE_LABELS[typeKey]} />
        ))}
      </Tabs>

      <Stack spacing={1}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '35%' }}>Ключ поля</TableCell>
                <TableCell>Заголовок в отчёте</TableCell>
                <TableCell align="right" sx={{ width: 72 }}>
                  Действия
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeDeviceColumns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                    Для этого типа используются общие колонки.
                  </TableCell>
                </TableRow>
              )}
              {activeDeviceColumns.map((column, columnIndex) => (
                <TableRow key={column.id}>
                  <TableCell>
                    <Autocomplete
                      freeSolo
                      options={activeDeviceOptions}
                      value={column.key || ''}
                      onInputChange={(_, newValue) =>
                        setDeviceTypeColumnKey(activeDeviceType, column.id, newValue || '')
                      }
                      onChange={(_, newValue) =>
                        setDeviceTypeColumnKey(
                          activeDeviceType,
                          column.id,
                          typeof newValue === 'string' ? newValue : newValue || '',
                          { autoTitle: true }
                        )
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div>
                            <div>{option}</div>
                            {activeDeviceHints[option] && (
                              <Typography variant="caption" color="text.secondary">
                                {activeDeviceHints[option]}
                              </Typography>
                            )}
                          </div>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ключ поля"
                          size="small"
                          fullWidth
                          disabled={disabled}
                          error={Boolean(activeDeviceError)}
                          placeholder="например, apartment_number"
                        />
                      )}
                      disabled={disabled}
                      clearOnBlur={false}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={column.title}
                      onChange={(event) =>
                        editDeviceTypeColumn(
                          activeDeviceType,
                          column.id,
                          'title',
                          event.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      disabled={disabled}
                      error={Boolean(activeDeviceError) && !column.title?.trim()}
                      placeholder="Например, № квартиры"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                      <IconButton
                        aria-label="Переместить вверх"
                        onClick={() => moveDeviceTypeColumn(activeDeviceType, column.id, -1)}
                        disabled={disabled || columnIndex === 0}
                        size="small"
                      >
                        <ArrowUpwardRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Переместить вниз"
                        onClick={() => moveDeviceTypeColumn(activeDeviceType, column.id, 1)}
                        disabled={disabled || columnIndex === activeDeviceColumns.length - 1}
                        size="small"
                      >
                        <ArrowDownwardRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        aria-label="Удалить колонку"
                        onClick={() => removeDeviceTypeColumn(activeDeviceType, column.id)}
                        disabled={disabled}
                        size="small"
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {activeDeviceError && (
          <Typography variant="caption" color="error">
            {activeDeviceError}
          </Typography>
        )}
        <Box>
          <Button
            startIcon={<AddCircleOutlineRoundedIcon />}
            variant="outlined"
            size="small"
            onClick={() => addDeviceTypeColumn(activeDeviceType)}
            disabled={disabled}
          >
            Добавить колонку
          </Button>
        </Box>
      </Stack>
        </>
      )}
    </Box>
  );
};

export default ReportConfigFormSection;
